import React, { useState, useEffect } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { 
  FileText, 
  Maximize2,
  ExternalLink,
  Loader2,
  Eye
} from 'lucide-react';

const SampleLessonViewer = () => {
  const [sampleNote, setSampleNote] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadSampleNote();
  }, []);

  const loadSampleNote = async () => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      const response = await fetch(`${backendUrl}/api/public/sample-lesson-note`);
      
      if (response.ok) {
        const data = await response.json();
        setSampleNote(data);
      } else {
        setSampleNote(null);
      }
    } catch (error) {
      console.error('Error loading sample lesson note:', error);
      setSampleNote(null);
    } finally {
      setLoading(false);
    }
  };

  const openPdfInNewTab = () => {
    if (!sampleNote || !sampleNote.file_data) return;

    try {
      // Convert base64 to blob
      const byteCharacters = atob(sampleNote.file_data);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: 'application/pdf' });
      
      // Create blob URL and open in new tab
      const blobUrl = URL.createObjectURL(blob);
      
      // Open in new tab
      const newTab = window.open(blobUrl, '_blank');
      
      // Clean up blob URL after a delay
      if (newTab) {
        setTimeout(() => {
          URL.revokeObjectURL(blobUrl);
        }, 10000);
      }
    } catch (error) {
      console.error('Error opening PDF:', error);
      alert('Failed to open PDF. Please try again.');
    }
  };

  if (loading) {
    return (
      <Card className="p-6 flex items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-blue-500" />
      </Card>
    );
  }

  if (!sampleNote) {
    return null; // Don't show if no sample note uploaded
  }

  return (
    <Card className="relative overflow-hidden shadow-xl bg-gradient-to-br from-blue-50 to-purple-50 border border-blue-200">
      {/* Content */}
      <div className="p-4">
        {/* Header */}
        <div className="flex items-center gap-2 mb-3">
          <div className="bg-red-100 p-2 rounded-lg">
            <FileText className="h-5 w-5 text-red-600" />
          </div>
          <div>
            <h3 className="font-bold text-sm text-gray-800">{sampleNote.title}</h3>
            <p className="text-xs text-gray-500">Sample Lesson Note (PDF)</p>
          </div>
        </div>

        {/* Description */}
        {sampleNote.description && (
          <p className="text-gray-600 text-xs mb-3 line-clamp-2">{sampleNote.description}</p>
        )}

        {/* Preview illustration - compact */}
        <div className="bg-white rounded-lg p-3 mb-3 border border-gray-200">
          <div className="flex items-center justify-center py-4">
            <div className="text-center">
              <div className="bg-gray-100 rounded-lg p-3 inline-block mb-2">
                <FileText className="h-10 w-10 text-gray-400" />
              </div>
              <p className="text-xs text-gray-500">Click below to view</p>
            </div>
          </div>
        </div>

        {/* View Button */}
        <Button 
          onClick={openPdfInNewTab}
          className="w-full bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white py-2 text-sm"
        >
          <Eye className="h-4 w-4 mr-2" />
          View Sample
          <ExternalLink className="h-3 w-3 ml-2" />
        </Button>

        {/* Note */}
        <p className="text-xs text-gray-400 text-center mt-2">
          Opens in new tab
        </p>
      </div>

      {/* Decorative corner */}
      <div className="absolute top-0 right-0 w-16 h-16 bg-gradient-to-bl from-blue-200/50 to-transparent" />
    </Card>
  );
};

export default SampleLessonViewer;
